import { FinancialLeasePaymentFields } from './financial-lease-payment-fields.js';

export class FinancialLeaseCalculator {
    /**
     * @type {HTMLFormElement}
     */
    #form;

    /**
     * @type {HTMLInputElement|null}
     */
    #grossInvestmentInput;

    /**
     * @type {Array<Element>}
     */
    #targets;

    /**
     * @type {AbortController|null}
     */
    #currentFetchController;

    /**
     * @type {HTMLButtonElement|null}
     */
    #submitButton;

    /**
     * @param {HTMLFormElement} form
     */
    constructor(form) {
        this.#form = form;
        this.#grossInvestmentInput = form.querySelector('[name*="grossInvestment"]');
        this.#targets = Array.from(document.querySelectorAll(form.dataset.target));
        this.#currentFetchController = null;
        this.#submitButton = document.querySelector(`button[form="${this.#form.id}"]`);
    }

    setup() {
        new FinancialLeasePaymentFields(this.#form, () => this.#clearFormErrors()).setup();
        this.#bindFormInputEvents();
        this.#hideSubmitButton();

        setTimeout(() => {
            this.#form.dispatchEvent(new Event('input'));
        }, 50);
    }

    #bindFormInputEvents() {
        this.#form.addEventListener(
            'input',
            debounce(() => {
                this.#performCalculation();
            }, 50),
        );
    }

    #performCalculation() {
        const calculationUrl = this.#form.dataset.financialLeaseCalculator;
        const abortController = new AbortController();

        if (this.#currentFetchController) {
            this.#currentFetchController.abort('Already running a calculation.');
        }

        if (!this.#grossInvestmentInput?.value) {
            return;
        }

        if (Number.parseFloat(this.#grossInvestmentInput.value) < 1) {
            return;
        }

        this.#clearFormErrors();
        this.#currentFetchController = abortController;

        fetch(calculationUrl, {
            method: 'POST',
            body: new FormData(this.#form),
            signal: abortController.signal,
        })
            .then((response) => {
                if (!response.ok) {
                    if (response.headers.get('Content-Type').includes('application/json')) {
                        return Promise.reject(response);
                    }

                    console.error(response);
                    throw new Error('Failed to calculate due to an invalid response from the server');
                }

                return response.json();
            })
            .then((json) => {
                for (const target of this.#targets) {
                    if (target.tagName === 'INPUT') {
                        target.value = json.monthlyPayment;
                    } else {
                        target.innerText = json.monthlyPayment;
                    }
                }

                if (this.#submitButton) {
                    this.#submitButton.disabled = false;
                }
            })
            .catch(async (exception) => {
                if (typeof exception === 'string' && exception.includes('Aborted')) {
                    console.debug('FinancialLeaseCalculator | Calculation aborted', exception);
                    return;
                }

                if (!(exception instanceof Response)) {
                    console.error('FinancialLeaseCalculator | Unexpected error', exception);
                    return;
                }

                const errors = await exception.json();

                if (!Object.hasOwn(errors, 'formErrors')) {
                    console.error('FinancialLeaseCalculator | Unexpected error response', {
                        formId: this.#form.id,
                        errors,
                    });
                    return;
                }

                if (this.#submitButton) {
                    this.#submitButton.disabled = true;
                }

                this.#displayFormErrors(errors.formErrors);
            });
    }

    #clearFormErrors() {
        for (const formFieldWithError of this.#form.querySelectorAll('.form-field.error')) {
            formFieldWithError.classList.remove('error');
        }

        for (const formErrorContainer of this.#form.querySelectorAll('.form-field-error-container')) {
            formErrorContainer.remove();
        }
    }

    /**
     * @param {Object<string, Array<string>>} formErrors
     */
    #displayFormErrors(formErrors) {
        for (const fieldName in formErrors) {
            const messages = formErrors[fieldName];
            const formField = this.#form.querySelector(`[name*="${fieldName}"]`);
            formField.classList.add('error');

            const formFieldErrorContainer = document.createElement('ul');
            formFieldErrorContainer.classList.add('form-field-error-container');

            for (const message of messages) {
                const formFieldError = document.createElement('li');
                formFieldError.classList.add('text-sm', 'text-red-600', 'dark:text-red-500');
                formFieldError.innerText = message;
                formFieldErrorContainer.appendChild(formFieldError);
            }

            formField.closest('.form-group').querySelector(`#${formField.id}-error`)?.append(formFieldErrorContainer);
        }
    }

    #hideSubmitButton() {
        const submitButton = this.#form.querySelector('[type=submit]');
        if (submitButton !== null) {
            submitButton.hidden = true;
            submitButton.classList.add('hidden');
            submitButton.classList.remove('action-btn');
        }
    }
}

export function init(doc = document) {
    for (const form of doc.querySelectorAll('[data-financial-lease-calculator]')) {
        new FinancialLeaseCalculator(form).setup();
    }
}

/**
 * Debounce function for delaying function execution
 * @template T
 * @param {(...args: T[]) => void} fn
 * @param {number} delay
 * @returns {(...args: T[]) => void}
 */
export function debounce(fn, delay = 50) {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}
